import { useEffect, useMemo } from 'react'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useParams } from 'react-router-dom'

import { connect } from 'react-redux'
import { deleteSearchParams, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { usePlaceByName, useResolvedPlaceName } from 'hooks/usePlace'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Places from 'constants/places'
import { isResolvedPlace } from 'lib/places/placeCacheUtils'
import { ANYWHERE_NAME } from 'constants/search'
import ExperienceSearchPage from '../ExperiencesSearch/ExperienceSearchPage'
import {
  normaliseLocationSlugForLookup,
  resolveCategoryCodesFromSlug,
} from '../../../lib/experiencesListUtils'

type RouteParams = {
  regionCode: string
  locationSlug: string
  categorySlug?: string
}

interface MappedStateProps {
  windowSearch: string
  placesLoading: App.State['destination']['placesLoading']
  categories: App.State['experience']['experienceCategories']
}

type Props = MappedStateProps

function ExperiencesListPage(props: Props) {
  const { locationSlug = '', categorySlug } = useParams<keyof RouteParams>()
  const {
    windowSearch: search,
    categories,
    placesLoading,
  } = props
  const navigation = useAppNavigation()
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search
  const searchParams = useMemo(() => new URLSearchParams(normalizedSearch), [normalizedSearch])

  const locationKey = useMemo(() => {
    return normaliseLocationSlugForLookup(locationSlug)
  }, [locationSlug])

  const isAnywhereSlug = locationKey === ANYWHERE_NAME.toLowerCase()
  const fetchedPlace = usePlaceByName(isAnywhereSlug ? undefined : locationKey)
  const destinationPlace = isAnywhereSlug ? Places.Anywhere : fetchedPlace
  const isFetchingPlace = placesLoading[locationKey]
  const { name: cachedDestinationName } = useResolvedPlaceName(
    !isAnywhereSlug && destinationPlace?.id ? destinationPlace.id : undefined,
  )

  const resolvedCategoryCodes = useMemo(() => {
    if (!categorySlug) return undefined
    if (!categories?.length) return undefined

    return resolveCategoryCodesFromSlug({
      categorySlug,
      categories,
    })
  }, [categorySlug, categories])

  const nextSearch = useMemo(() => {
    const hasDestinationParams = searchParams.has('destinationId')
    const hasExperienceParams = searchParams.has('experienceId')
    const hasCategoryParams = searchParams.has('categories')

    if (!destinationPlace || !('id' in destinationPlace) || !destinationPlace.id) {
      if (hasDestinationParams) {
        return normalizedSearch
      }
      return deleteSearchParams(normalizedSearch, 'destinationId', 'destinationName', 'categories', 'experienceId', 'experienceName')
    }

    let updatedSearch = normalizedSearch

    if (!hasDestinationParams) {
      updatedSearch = setSearchParamValue(updatedSearch, 'destinationId', destinationPlace.id)
      const resolvedDestinationName: string | undefined = isAnywhereSlug
        ? ANYWHERE_NAME
        : isResolvedPlace(destinationPlace)
          ? destinationPlace.name
          : cachedDestinationName
      if (resolvedDestinationName) {
        updatedSearch = setSearchParamValue(updatedSearch, 'destinationName', resolvedDestinationName)
      }
    }

    if (!hasExperienceParams) {
      updatedSearch = deleteSearchParams(updatedSearch, 'experienceId', 'experienceName')
    }

    if (!hasCategoryParams) {
      if (resolvedCategoryCodes?.length) {
        updatedSearch = setSearchParamValue(updatedSearch, 'categories', resolvedCategoryCodes.join(','))
        return updatedSearch
      }

      return deleteSearchParams(updatedSearch, 'categories')
    }

    return updatedSearch
  }, [cachedDestinationName, destinationPlace, isAnywhereSlug, normalizedSearch, resolvedCategoryCodes, searchParams])

  useEffect(() => {
    if (nextSearch !== normalizedSearch) {
      navigation.replace({
        search: nextSearch ? `?${nextSearch}` : '',
      })
    }
  }, [navigation, nextSearch, normalizedSearch])
  if (nextSearch !== normalizedSearch) {
    return <LoadingIndicator />
  }
  if (!destinationPlace && isFetchingPlace) {
    return <LoadingIndicator />
  }
  return <ExperienceSearchPage />
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    windowSearch: state.router.location.search,
    placesLoading: state.destination.placesLoading,
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperiencesListPage)
